import { type ColumnDef, flexRender } from "@tanstack/react-table";

/**
 * Wraps every cell of a column list in one block that may break a long code or id. Under 720 px the kit table shows a row as a card and each
 * cell as a flex row: every child of the cell would be its own flex item, side by side, and a long code (`PAYMENT_REQUEST_RECONCILE_FAILED`) would
 * push the card wider than the screen. One wrapper makes the content one item that wraps. On a wide screen the wrapper changes nothing.
 * On a wide table a word breaks only when it does not fit (a column is not squeezed); in the card layout it may break anywhere, because a card
 * is as wide as the longest unbreakable word otherwise.
 * The result must be a module constant (the kit draws a cell with `flexRender`, which treats a cell function as a component).
 */
// biome-ignore lint/suspicious/noExplicitAny: the value type differs per column
type AnyColumn<TData> = ColumnDef<TData, any>;

export function wrapCells<TData>(columns: readonly AnyColumn<TData>[]): AnyColumn<TData>[] {
  return columns.map((column) => {
    const inner = column.cell;
    if (typeof inner !== "function") return column;
    return {
      ...column,
      cell: (context) => (
        <div className="min-w-0 break-words @max-[720px]/dt:[overflow-wrap:anywhere]">
          {flexRender(inner, context)}
        </div>
      ),
    } as ColumnDef<TData, unknown>;
  });
}
